import { ProductVariant } from 'api/products/_requests';
import React, { useState } from 'react';
import { Card, Form, Table } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'feather-icons-react';

interface ProductVariantCardProps {
  variantGroupType: string;
  setVariantGroupType: (variantGroupType: string) => void;
  variantGroupValues: string[];
  variants: ProductVariant[];
  setVariants: (variants: ProductVariant[]) => void;
  checkedVariants: { [key: number]: boolean };
  setCheckedVariants: (checkedVariants: { [key: number]: boolean }) => void;
}

const ProductVariantCard = ({
  variantGroupType,
  setVariantGroupType,
  variantGroupValues,
  variants,
  setVariants,
  checkedVariants,
  setCheckedVariants
}: ProductVariantCardProps) => {
  const [expandedGroups, setExpandedGroups] = useState<{
    [key: string]: boolean;
  }>({});
  const [checkedGroups, setCheckedGroups] = useState<{
    [key: string]: boolean;
  }>({});

  const [allChecked, setAllChecked] = useState(false);

  // Group variants by the selected group type
  const groupedVariants = variants.reduce(
    (acc, variant, idx) => {
      const groupKey = variant[
        variantGroupType as keyof ProductVariant
      ] as string;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push({ ...variant, _idx: idx }); // keep index for checkbox
      return acc;
    },
    {} as { [key: string]: (ProductVariant & { _idx: number })[] }
  );

  // Handle expand/collapse
  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Handle checkboxes
  const handleCheckAll = () => {
    const newChecked = !allChecked;
    setAllChecked(newChecked);
    setCheckedGroups(
      Object.keys(groupedVariants).reduce(
        (acc, key) => {
          acc[key] = newChecked;
          return acc;
        },
        {} as { [key: string]: boolean }
      )
    );
    setCheckedVariants(
      variants.reduce(
        (acc, _, idx) => {
          acc[idx] = newChecked;
          return acc;
        },
        {} as { [key: number]: boolean }
      )
    );
  };

  const handleCheckGroup = (groupKey: string) => {
    const newChecked = !checkedGroups[groupKey];
    setCheckedGroups(prev => ({ ...prev, [groupKey]: newChecked }));
    const updatedCheckedVariants = { ...checkedVariants };
    groupedVariants[groupKey].forEach(v => {
      updatedCheckedVariants[v._idx] = newChecked;
    });
    setCheckedVariants(updatedCheckedVariants);
  };

  const handleGroupVariantChange = (
    groupKey: string,
    field: keyof ProductVariant,
    value: string | number | boolean
  ) => {
    const updatedVariants = [...variants];
    updatedVariants.forEach(v => {
      if (v[variantGroupType as keyof ProductVariant] === groupKey) {
        v[field] = value as never;
      }
    });
    setVariants(updatedVariants);
  };

  const handleCheckVariant = (idx: number) => {
    const updatedCheckedVariants = { ...checkedVariants };
    updatedCheckedVariants[idx] = !updatedCheckedVariants[idx];
    setCheckedVariants(updatedCheckedVariants);
  };

  // Handle price/available changes
  const handleVariantChange = (
    index: number,
    field: keyof ProductVariant,
    value: string | number | boolean
  ) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: value
    };
    setVariants(updatedVariants);
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 mb-3">
          <span className="text-nowrap">Group by</span>
          <select
            className="form-select w-100 w-sm-auto"
            value={variantGroupType}
            onChange={e => setVariantGroupType(e.target.value)}
          >
            {variantGroupValues.map(value => (
              <option key={value} value={value}>
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </option>
            ))}
          </select>
        </div>
        <div className="table-responsive">
          <Table bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allChecked}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>
                  Variant &middot;{' '}
                  <span
                    className="text-primary cursor-pointer"
                    onClick={() =>
                      setExpandedGroups(
                        Object.keys(groupedVariants).reduce(
                          (acc, key) => {
                            acc[key] = false;
                            return acc;
                          },
                          {} as { [key: string]: boolean }
                        )
                      )
                    }
                  >
                    collapse all
                  </span>
                </th>
                <th className="min-w-180">Price</th>
                <th className="min-w-180">Sale Price</th>
                <th className="min-w-50">
                  <span className="text-muted">In Stock</span>
                </th>
                <th className="min-w-120">
                  <span className="text-muted">Stock Qty</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedVariants).map(
                ([groupKey, groupVariants]) => (
                  <React.Fragment key={groupKey}>
                    <tr className="bg-light">
                      <td className="text-center p-0">
                        <Form.Check
                          type="checkbox"
                          checked={!!checkedGroups[groupKey]}
                          onChange={() => handleCheckGroup(groupKey)}
                        />
                      </td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleGroup(groupKey)}
                      >
                        {expandedGroups[groupKey] ? (
                          <ChevronDown size={16} className="me-1" />
                        ) : (
                          <ChevronRight size={16} className="me-1" />
                        )}
                        <span className="fw-semibold text-uppercase">
                          {groupKey}
                        </span>
                        <span
                          className="text-muted ms-2"
                          style={{ fontSize: 12 }}
                        >
                          {groupVariants.length} variant
                          {groupVariants.length > 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            type="number"
                            onChange={e =>
                              handleGroupVariantChange(
                                groupKey,
                                'price',
                                e.target.value
                              )
                            }
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </td>
                      <td>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            type="number"
                            onChange={e =>
                              handleGroupVariantChange(
                                groupKey,
                                'discountedPrice',
                                e.target.value
                              )
                            }
                            min="0"
                            step="0.01"
                          />
                        </div>
                      </td>
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          onChange={e =>
                            handleGroupVariantChange(
                              groupKey,
                              'inStock',
                              (e.target as HTMLInputElement).checked
                            )
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Form.Control
                          type="number"
                          size="sm"
                          onChange={e =>
                            handleGroupVariantChange(
                              groupKey,
                              'stockQuantity',
                              parseInt(e.target.value)
                            )
                          }
                          min="0"
                        />
                      </td>
                    </tr>
                    {expandedGroups[groupKey] &&
                      groupVariants.map(variant => (
                        <tr key={variant._idx}>
                          <td className="text-center p-0">
                            <Form.Check
                              type="checkbox"
                              checked={!!checkedVariants[variant._idx]}
                              onChange={() => handleCheckVariant(variant._idx)}
                            />
                          </td>
                          <td className="ps-4 text-uppercase">
                            {[variant.color, variant.size, variant.weight]
                              .filter(Boolean)
                              .join(' / ')}
                          </td>
                          <td>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text">$</span>
                              <Form.Control
                                type="number"
                                value={variant.price}
                                onChange={e =>
                                  handleVariantChange(
                                    variant._idx,
                                    'price',
                                    parseFloat(e.target.value)
                                  )
                                }
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </td>
                          <td>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text">$</span>
                              <Form.Control
                                type="number"
                                value={variant.discountedPrice}
                                onChange={e =>
                                  handleVariantChange(
                                    variant._idx,
                                    'discountedPrice',
                                    parseInt(e.target.value)
                                  )
                                }
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </td>
                          <td className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={variant.inStock}
                              onChange={e =>
                                handleVariantChange(
                                  variant._idx,
                                  'inStock',
                                  e.target.checked
                                )
                              }
                            />
                          </td>
                          <td className="p-2">
                            <Form.Control
                              type="number"
                              size="sm"
                              value={variant.stockQuantity}
                              onChange={e =>
                                handleVariantChange(
                                  variant._idx,
                                  'stockQuantity',
                                  parseInt(e.target.value)
                                )
                              }
                              min="0"
                            />
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                )
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductVariantCard;
